import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { clearAdminAuthFlow, getAdminAuthUser } from '../../services/storage/adminFlow';
import { isSuperAdminRole, logoutAdminAuthSession } from '../../services/api/admin';
import '../../styles/admin.css';

interface AdminShellProps {
  badge: string;
  title: string;
  copy: string;
  children: React.ReactNode;
}

type AdminNavIcon =
  | 'overview'
  | 'engagement'
  | 'declaredLoads'
  | 'campaigns'
  | 'promotions'
  | 'communications'
  | 'review'
  | 'onboarding'
  | 'face'
  | 'business'
  | 'payout'
  | 'support'
  | 'whatsappBookings'
  | 'trips'
  | 'coverage'
  | 'drivers'
  | 'users'
  | 'finance'
  | 'payments';

const ADMIN_NAV: Array<{ to: string; label: string; icon: AdminNavIcon }> = [
  { to: '/admin', label: 'Overview', icon: 'overview' },
  { to: '/admin/trips', label: 'Trips', icon: 'trips' },
  { to: '/admin/whatsapp-bookings', label: 'WhatsApp Bookings', icon: 'whatsappBookings' },
  { to: '/admin/support', label: 'Support', icon: 'support' },
  { to: '/admin/engagement', label: 'Engagement', icon: 'engagement' },
  { to: '/admin/declared-loads', label: 'Declared Loads', icon: 'declaredLoads' },
  { to: '/admin/driver-campaigns', label: 'Campaigns', icon: 'campaigns' },
  { to: '/admin/customer-promotions', label: 'Promotions', icon: 'promotions' },
  { to: '/admin/communications', label: 'Communications', icon: 'communications' },
  { to: '/admin/review-queue', label: 'Review Queue', icon: 'review' },
  { to: '/admin/driver-onboarding', label: 'Onboard Drivers', icon: 'onboarding' },
  { to: '/admin/face-liveness-reviews', label: 'Face Reviews', icon: 'face' },
  { to: '/admin/business-profiles', label: 'Business Profiles', icon: 'business' },
  { to: '/admin/payout-reviews', label: 'Payout Reviews', icon: 'payout' },
  { to: '/admin/driver-coverage', label: 'Coverage', icon: 'coverage' },
  { to: '/admin/drivers', label: 'Drivers', icon: 'drivers' },
  { to: '/admin/users', label: 'Users', icon: 'users' },
  { to: '/admin/finance', label: 'Finance', icon: 'finance' },
  { to: '/admin/payment-ops', label: 'Payment Ops', icon: 'payments' },
];

const AdminNavIconGlyph: React.FC<{ icon: AdminNavIcon }> = ({ icon }) => {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg aria-hidden="true" className="admin-nav-icon" focusable="false" viewBox="0 0 24 24">
      {icon === 'overview' ? (
        <>
          <rect {...common} height="7" rx="1.5" width="7" x="3" y="3" />
          <rect {...common} height="7" rx="1.5" width="7" x="14" y="3" />
          <rect {...common} height="7" rx="1.5" width="7" x="3" y="14" />
          <rect {...common} height="7" rx="1.5" width="7" x="14" y="14" />
        </>
      ) : null}
      {icon === 'engagement' ? (
        <>
          <path {...common} d="M4 19V5" />
          <path {...common} d="M4 19h16" />
          <path {...common} d="m7 15 4-4 3 3 5-7" />
        </>
      ) : null}
      {icon === 'declaredLoads' ? (
        <>
          <path {...common} d="M4 7h10" />
          <path {...common} d="M4 12h8" />
          <path {...common} d="M4 17h6" />
          <path {...common} d="M15 13h5l-1.5 5h-7z" />
          <path {...common} d="M16 13l1-4h2" />
          <circle {...common} cx="13.5" cy="20" r=".5" />
          <circle {...common} cx="18.5" cy="20" r=".5" />
        </>
      ) : null}
      {icon === 'communications' ? (
        <>
          <path {...common} d="M4 6h16v12H4z" />
          <path {...common} d="m4 7 8 6 8-6" />
        </>
      ) : null}
      {icon === 'campaigns' ? (
        <>
          <path {...common} d="M4 11v6a2 2 0 0 0 2 2h1" />
          <path {...common} d="M7 11 19 5v14L7 13z" />
          <path {...common} d="M10 14l2 5" />
        </>
      ) : null}
      {icon === 'promotions' ? (
        <>
          <path {...common} d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
          <path {...common} d="M4 8h16v4H4z" />
          <path {...common} d="M12 8v13" />
          <path {...common} d="M12 8H8.5A2.5 2.5 0 1 1 12 5.5z" />
          <path {...common} d="M12 8h3.5A2.5 2.5 0 1 0 12 5.5z" />
        </>
      ) : null}
      {icon === 'review' ? (
        <>
          <path {...common} d="M7 4h10l2 2v14H5V6z" />
          <path {...common} d="M8 10h8" />
          <path {...common} d="M8 14h5" />
        </>
      ) : null}
      {icon === 'onboarding' ? (
        <>
          <path {...common} d="M7 4h8l4 4v12H5V6a2 2 0 0 1 2-2z" />
          <path {...common} d="M15 4v5h5" />
          <path {...common} d="M9 14h6" />
          <path {...common} d="M12 11v6" />
        </>
      ) : null}
      {icon === 'face' ? (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <path {...common} d="M9 10h.01" />
          <path {...common} d="M15 10h.01" />
          <path {...common} d="M9 15c1.7 1.2 4.3 1.2 6 0" />
        </>
      ) : null}
      {icon === 'business' ? (
        <>
          <path {...common} d="M4 20V8l8-4 8 4v12" />
          <path {...common} d="M9 20v-6h6v6" />
          <path {...common} d="M8 10h.01" />
          <path {...common} d="M12 10h.01" />
          <path {...common} d="M16 10h.01" />
        </>
      ) : null}
      {icon === 'payout' ? (
        <>
          <rect {...common} height="12" rx="2" width="18" x="3" y="6" />
          <path {...common} d="M7 10h5" />
          <path {...common} d="M16 14h1" />
        </>
      ) : null}
      {icon === 'support' ? (
        <>
          <path {...common} d="M5 12a7 7 0 0 1 14 0v4a2 2 0 0 1-2 2h-2" />
          <path {...common} d="M7 12v4H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h2z" />
          <path {...common} d="M17 10h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2z" />
          <path {...common} d="M12 18h3" />
        </>
      ) : null}
      {icon === 'whatsappBookings' ? (
        <>
          <path {...common} d="M5 5h14v10H8l-3 3z" />
          <path {...common} d="M9 9h6" />
          <path {...common} d="M9 12h4" />
          <path {...common} d="M16 16l3 3" />
          <path {...common} d="M19 16l-3 3" />
        </>
      ) : null}
      {icon === 'trips' ? (
        <>
          <path {...common} d="M5 17h14" />
          <path {...common} d="M7 17l2-8h6l2 8" />
          <path {...common} d="M8 17v2" />
          <path {...common} d="M16 17v2" />
          <path {...common} d="M9 12h6" />
        </>
      ) : null}
      {icon === 'coverage' ? (
        <>
          <path {...common} d="M12 21s7-5.3 7-11a7 7 0 0 0-14 0c0 5.7 7 11 7 11z" />
          <circle {...common} cx="12" cy="10" r="2.5" />
        </>
      ) : null}
      {icon === 'drivers' ? (
        <>
          <circle {...common} cx="9" cy="8" r="3" />
          <path {...common} d="M4 20a5 5 0 0 1 10 0" />
          <path {...common} d="M17 9h3" />
          <path {...common} d="M18.5 7.5v3" />
          <path {...common} d="M17 17h4" />
        </>
      ) : null}
      {icon === 'users' ? (
        <>
          <circle {...common} cx="9" cy="8" r="3" />
          <path {...common} d="M3.5 20a5.5 5.5 0 0 1 11 0" />
          <circle {...common} cx="17" cy="9" r="2.5" />
          <path {...common} d="M14.5 20a4.5 4.5 0 0 1 6.5-4" />
        </>
      ) : null}
      {icon === 'finance' ? (
        <>
          <path {...common} d="M4 19h16" />
          <path {...common} d="M7 16V9" />
          <path {...common} d="M12 16V5" />
          <path {...common} d="M17 16v-4" />
        </>
      ) : null}
      {icon === 'payments' ? (
        <>
          <rect {...common} height="14" rx="2" width="18" x="3" y="5" />
          <path {...common} d="M3 10h18" />
          <path {...common} d="M7 15h3" />
        </>
      ) : null}
    </svg>
  );
};

const AdminShell: React.FC<AdminShellProps> = ({ badge, title, copy, children }) => {
  const adminUser = getAdminAuthUser();
  const navItems = isSuperAdminRole(adminUser?.roles)
    ? ADMIN_NAV
    : ADMIN_NAV.filter((item) => item.to !== '/admin/finance' && item.to !== '/admin/payment-ops');

  return (
    <section className="admin-page">
      <div className="admin-shell">
        <aside className="admin-sidebar">
          <div className="admin-sidebar-brand">
            <div className="admin-sidebar-badge">Admin Console</div>
            <strong>{adminUser?.name || 'ShiftWagon Admin'}</strong>
            <span>{adminUser?.email || 'Admin access'}</span>
          </div>

          <nav className="admin-nav" aria-label="Admin navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/admin'}
                className={({ isActive }) => `admin-nav-link${isActive ? ' admin-nav-link-active' : ''}`}
              >
                <AdminNavIconGlyph icon={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="admin-sidebar-footer">
            <Link className="register-secondary-button" to="/">
              Home
            </Link>
            <Link
              className="register-secondary-button"
              onClick={() => {
                void logoutAdminAuthSession().catch(() => clearAdminAuthFlow());
              }}
              to="/login"
            >
              Logout
            </Link>
          </div>
        </aside>

        <div className="admin-main">
          <header className="admin-header">
            <div className="register-form-badge">{badge}</div>
            <h1 className="admin-title">{title}</h1>
            <p className="admin-copy">{copy}</p>
          </header>
          {children}
        </div>
      </div>
    </section>
  );
};

export default AdminShell;
